import React, { useState, useCallback, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { authFetch, API_BASE } from "../api";

/**
 * Modal: run a recipe's credential validator with user-provided values.
 *
 * Transient-by-design — values are sent to the server for testing but NOT
 * persisted. This matches the typical flow: user just finished configuring
 * their cloud provider and wants to verify their keys work before
 * committing to a full app.
 *
 * Shows stepwise results so the user sees exactly where auth breaks.
 */
export default function CredentialsTestModal({ recipe, onClose, initialValues, onPass }) {
  // Pre-fill from the last passing test (if the user is re-opening this modal)
  const [values, setValues] = useState(() => ({ ...(initialValues || {}) }));
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateField = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const runTest = useCallback(async () => {
    setRunning(true);
    setError(null);
    setResults(null);
    try {
      const res = await authFetch(`${API_BASE}/recipes/${recipe.id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secrets: values }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
      } else {
        setResults(data);
        // If ALL steps passed, hand the values back to the parent so the
        // Create Dashboard modal can auto-save them as secrets
        if (data.ok && onPass) {
          onPass({ ...values });
        }
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setRunning(false);
    }
  }, [recipe.id, values, onPass]);

  const statusIcon = (status) => {
    if (status === "pass") return "✓";
    if (status === "fail") return "✗";
    if (status === "skip") return "○";
    return "…";
  };

  const requiredSecrets = Array.isArray(recipe.requiredSecrets) ? recipe.requiredSecrets : [];
  const allFilled = requiredSecrets.every((s) => values[s.name] && values[s.name].trim());

  return (
    <div className="recipe-modal-backdrop" onClick={onClose}>
      <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recipe-modal-header">
          <h2>Test {recipe.name} credentials</h2>
          <button className="recipe-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="recipe-modal-body">
          <p className="recipe-modal-desc">
            Enter your credentials below — we'll validate them step-by-step without saving anything.
            If any step fails, you'll see a specific hint for what to fix.
          </p>

          {requiredSecrets.map((secret) => (
            <div key={secret.name} className="recipe-modal-field">
              <label>
                <span className="recipe-modal-field-name">
                  <code>{secret.name}</code>
                </span>
                <span className="recipe-modal-field-desc">{secret.description}</span>
              </label>
              <input
                type={secret.name.toUpperCase().includes("SECRET") ? "password" : "text"}
                value={values[secret.name] || ""}
                onChange={(e) => updateField(secret.name, e.target.value)}
                placeholder={secret.example || ""}
                disabled={running}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          ))}

          {error && <div className="recipe-modal-error">{error}</div>}

          {results && (
            <div className="recipe-modal-results">
              <h3>
                {results.ok
                  ? "✓ All checks passed — close this, click Create Dashboard, and these keys will be saved to your new app automatically."
                  : "✗ Some checks failed"}
              </h3>
              <ol>
                {results.steps.map((s, i) => (
                  <li key={i} className={`recipe-modal-step recipe-modal-step-${s.status}`}>
                    <span className="recipe-modal-step-icon">{statusIcon(s.status)}</span>
                    <div className="recipe-modal-step-body">
                      <div className="recipe-modal-step-name">{s.name}</div>
                      {s.error && <div className="recipe-modal-step-error">{s.error}</div>}
                      {s.hint && (
                        <div className="recipe-modal-step-hint">
                          <ReactMarkdown>{s.hint}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <div className="recipe-modal-footer">
          <button onClick={onClose} disabled={running}>Close</button>
          <button
            className="recipe-modal-primary"
            onClick={runTest}
            disabled={running || !allFilled}
          >
            {running ? "Testing…" : results ? "Retry" : "Run Test"}
          </button>
        </div>
      </div>
    </div>
  );
}
